import { FC } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@doda/io-react/common';
import { Bookmark } from '@doda/common/src/interfaces/api/scoutServiceJobs';
import {
  USER_CLICK_BOOKMARKLIST,
  USER_CLICK_JOBDETAIL,
  generateUrl,
} from 'components/FollowSearchResultsNavigation/logics/generateUrl';
import {
  DODA_SITE_URL,
  RECOMMEND_LIST_URL,
} from '@doda/common/src/constants/urls';
import { makeStaticUrl } from '@doda/common/src/logics/staticUrl';
import styles from './index.module.scss';
import { EmptyDisplay } from '../EmptyDisplay';
import { ACCORDION_ITEMS } from '../../constants';

/**
 * BookmarkList コンポーネントのプロパティ
 */
interface Props {
  /** ブックマークリスト */
  bookmarkList: Bookmark[];
  /** メニューを閉じるクリック関数 */
  onClickMenuClose: () => void;
  /** ログイン判定 */
  loginStatus: boolean;
}

/**
 * BookmarkList コンポーネント
 * @param {Props} props - BookmarkList コンポーネントのプロパティ
 * @returns {JSX.Element} レンダリングされたコンポーネント
 */
export const BookmarkList: FC<Props> = ({
  bookmarkList,
  onClickMenuClose,
  loginStatus,
}) => {
  const router = useRouter();

  /**
   * 求人詳細画面URLの生成関数
   * @param {string} jobDetailUrl - 求人詳細URL
   * @returns {string} 生成された求人詳細URL
   */
  const createJobSearchDetailUrl = (jobDetailUrl: string): string => {
    const path = DODA_SITE_URL.jobSearchDetail;
    // jobDetailUrlからクエリのみ抽出し配列化
    const params = jobDetailUrl.split('?')[1];
    const paramsArray = params.split('&').reduce((acc, cur) => {
      const [key, value] = cur.split('=');
      return { ...acc, [key]: value };
    }, {});
    const url = generateUrl({
      baseUrl: makeStaticUrl(paramsArray, path),
      userClickList: USER_CLICK_JOBDETAIL,
      pathname: router.pathname,
      loginStatus,
    });
    return url;
  };

  if (!bookmarkList.length) {
    // ブックマークリストが空の場合の表示
    return (
      <EmptyDisplay
        label={ACCORDION_ITEMS.bookmarkList.label}
        onClick={onClickMenuClose}
      />
    );
  }

  return (
    <div className={styles.bookmarkListContentsWrapper}>
      <ul className={styles.bookmarkListContentsWrapper__lists}>
        {bookmarkList?.map(({ wjoid, jobDetailUrl, wconm, wtitl }) => (
          <li
            key={wjoid}
            className={styles.bookmarkListContentsWrapper__listItem}
          >
            <a
              href={createJobSearchDetailUrl(jobDetailUrl)}
              target="_blank"
              rel="noreferrer"
            >
              <p className={styles.bookmarkListContentsWrapper__companyName}>
                {wconm}
              </p>
              <p className={styles.bookmarkListContentsWrapper__jobName}>
                {wtitl}
              </p>
            </a>
          </li>
        ))}
      </ul>
      <div className={styles.bookmarkListContentsWrapper__btmArea}>
        {/* TODO:装飾で下線が付くためラップしています */}
        <div className={styles.bookmarkListContentsWrapper__btnWrap}>
          <Button
            as="a"
            href={generateUrl({
              baseUrl: RECOMMEND_LIST_URL.bookmark_list,
              userClickList: USER_CLICK_BOOKMARKLIST,
              pathname: router.pathname,
              loginStatus,
            })}
            size="S"
            variant="green"
          >
            気になるリスト一覧へ
          </Button>
        </div>
      </div>
    </div>
  );
};
